import {envIsDev, envIsTest} from '../../config';
import type {ContentPart} from '../store/thread';

/** UnavailableReason explains why the agent chat feature is unavailable. */
export type UnavailableReason =
  | 'feature-flag-disabled'
  | 'org-setting-disabled'
  | 'loading';

/** AgentChatAvailability reports whether the agent chat feature is enabled. */
export interface AgentChatAvailability {
  /** Whether the feature is enabled (gate + org settings pass). */
  isAvailable: boolean;
  unavailableReason?: UnavailableReason;
}

/** SendMessageOptions controls how `sendMessage` delivers a prompt. */
export interface SendMessageOptions {
  /** Start a fresh thread before sending. */
  newChat?: boolean;
}

/**
 * WBAgentController is the set of agent chat capabilities exposed to the rest
 * of the app, such as opening the chat or sending a message.
 */
export interface WBAgentController {
  openChat(): void;
  sendMessage(
    // TODO: Accept an array of parts so the prompt can carry inline mentions.
    prompt: string,
    /** attachments are prepended to the prompt as leading message parts. */
    attachments?: ContentPart[],
    opts?: SendMessageOptions
  ): void;
}

/**
 * WBAgentControllerStore holds the agent chat's feature availability and the
 * imperative handle used to open the chat or send a message. One instance is
 * shared app-wide; tests instantiate their own for isolation.
 *
 * NOTE: Methods use arrow functions so they are bound; important when passing
 * to `useSyncExternalStore`.
 */
export class WBAgentControllerStore {
  private availability: AgentChatAvailability = {
    isAvailable: false,
    unavailableReason: 'loading',
  };

  private handle: WBAgentController | null = null;

  private readonly listeners = new Set<() => void>();

  getAvailability = (): AgentChatAvailability => this.availability;

  setAvailability = (next: AgentChatAvailability): void => {
    if (next === this.availability) {
      return;
    }
    this.availability = next;
    this.emit();
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * registerHandle installs the imperative handle and returns a disposer. Only
   * one handle is expected at a time (a single mount point); a second
   * registration in dev signals a duplicate mount.
   */
  registerHandle = (next: WBAgentController): (() => void) => {
    if (this.handle != null && (envIsDev || envIsTest)) {
      console.warn(
        'WBAgentController handle registered twice; expected a single agent chat mount point.'
      );
    }

    this.handle = next;
    return () => {
      if (this.handle === next) {
        this.handle = null;
      }
    };
  };

  getHandle = (): WBAgentController | null => this.handle;

  private emit(): void {
    // NOTE: Snapshot listeners to avoid mutation during iteration.
    for (const listener of Array.from(this.listeners)) {
      listener();
    }
  }
}

/** wbAgentControllerStore is the shared, app-wide controller store instance. */
export const wbAgentControllerStore = new WBAgentControllerStore();
